/**
 * @file types.ts
 * @description Standardized type definitions, enums, and interfaces for refrigerant analysis.
 */

/**
 * Enumeration of all refrigerant types supported in the database.
 * This enum should match the available fluids in the JSON files.
 */
export enum RefrigerantCode {
  R22 = 'R22',
  R32 = 'R32',
  R134a = 'R134a',
  R404A = 'R404A',
  R407C = 'R407C',
  R410A = 'R410A',
  R448A = 'R448A',
  R449A = 'R449A',
  R513A = 'R513A',
  R1234yf = 'R1234yf'
}

/**
 * Enumeration of all pressure units supported in the database.
 * Units ending with 'G' are gauge pressure (relative to atmospheric pressure).
 * Units ending with 'A' are absolute pressure (relative to vacuum).
 * Units without suffix are typically absolute pressure.
 */
export enum PressureUnit {
  // Absolute pressure units
  PAA = 'PAA',    // Pascals (absolute)
  KPAA = 'KPAA',  // Kilopascals (absolute)
  BARA = 'BARA',  // Bar (absolute)
  PSIA = 'PSIA',  // Pounds per square inch (absolute)
  
  // Gauge pressure units
  PAG = 'PAG',    // Pascals (gauge)
  KPAG = 'KPAG',  // Kilopascals (gauge)
  BARG = 'BARG',  // Bar (gauge)
  PSIG = 'PSIG',  // Pounds per square inch (gauge)

  // Vacuum pressure units
  INHG = 'INHG',  // Inches of mercury (vacuum)
  MMHG = 'MMHG',  // Millimeters of mercury (vacuum)
}

/**
 * Enumeration of all temperature units supported in the database.
 */
export enum TemperatureUnit {
  K = 'K',
  C = 'C',
  F = 'F',
}

export interface PressureValue {
  pressure: number;
  pressureUnit: PressureUnit;
}

export interface TemperatureValue {
  temperature: number;
  temperatureUnit: TemperatureUnit;
}

export enum RefrigerantPhase {
  Vapor = 'Vapor',
  Liquid = 'Liquid',
  Saturated = 'Saturated',
}

/**
 * Defines the possible thermodynamic states of the refrigerant at a given point.
 */
export enum RefrigerantState {
  Superheated = 'Superheated Vapor',
  Subcooled = 'Subcooled Liquid',
  Saturated = 'Saturated (Two-Phase)',
  Error = 'Error',
}

// Fluid Data Interfaces
export interface FluidInfo {
  "2DPNG_URL"?: string;
  ALIASES?: string[];
  CAS?: string;
  CHEMSPIDER_ID?: number;
  ENVIRONMENTAL?: {
    ASHRAE34: string;
    FH: number;
    GWP100: number;
    GWP20: number;
    GWP500: number;
    HH: number;
    Name: string;
    ODP: number;
    PH: number;
  };
  FORMULA?: string;
  INCHI_KEY?: string;
  INCHI_STRING?: string;
  NAME?: string;
  REFPROP_NAME?: string;
  SMILES?: string;
  [key: string]: any;
}

export interface FluidState {
  T: number;
  T_units: string;
  hmolar: number;
  hmolar_units: string;
  p: number;
  p_units: string;
  rhomolar: number;
  rhomolar_units: string;
  smolar: number;
  smolar_units: string;
}

export interface FluidStates {
  critical: FluidState;
  triple_liquid?: FluidState;
  triple_vapor?: FluidState;
  hs_anchor?: FluidState;
  reducing?: FluidState;
  sat_min_liquid?: FluidState;
  sat_min_vapor?: FluidState;
}

export interface AncillaryFunction {
  using_tau_r?: boolean;
  T_r?: number;
  Tmax?: number;
  Tmin?: number;
  A?: number[];
  B?: number[];
  _note?: string;
  max_abs_error?: number;
  max_abs_error_units?: string;
  type?: string;
  
  description?: string;
  max_abserror_percentage?: number;
  n?: number[];
  reducing_value?: number;
  t?: number[];
  BibTeX?: string;
  Tc?: number;
  a?: number[];
  [key: string]: any;
}

export interface Ancillaries {
  hL?: AncillaryFunction;
  hLV?: AncillaryFunction;
  pS?: AncillaryFunction;
  pL?: AncillaryFunction;
  pV?: AncillaryFunction;
  rhoL?: AncillaryFunction;
  rhoV?: AncillaryFunction;
  sL?: AncillaryFunction;
  sLV?: AncillaryFunction;
  surface_tension?: AncillaryFunction;
  [key: string]: any;
}

export interface EOSComponent {
  BibTeX_CP0?: string;
  BibTeX_EOS: string;
  STATES: {
    hs_anchor?: FluidState;
    reducing?: FluidState;
    sat_min_liquid?: FluidState;
    sat_min_vapor?: FluidState;
    [key: string]: any;
  };
  alpha0: any;
  alphar: any;
  gas_constant: number;
  gas_constant_units: string;
  molar_mass: number;
  molar_mass_units: string;
  p_max: number;
  p_max_units: string;
  pseudo_pure: boolean;
  [key: string]: any;
}

export interface TransportProperties {
  conductivity?: any;
  viscosity?: any;
  [key: string]: any;
}

export interface FluidData {
  ANCILLARIES: Ancillaries;
  EOS: EOSComponent[];
  INFO: FluidInfo;
  STATES: FluidStates;
  TRANSPORT?: TransportProperties;
  [key: string]: any;
}

export interface Recommendation {
  text: string;
  severity: 'critical' | 'warning' | 'info';
}